import * as yup from "yup";

/**
 * Draft schema: same keys as productValidationSchema but every field is nullable.
 * Accepts same signature to keep parity with the main schema.
 */

const asNullableNumber = () =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value
    )
    .nullable();

const productDraftSchema = ({ kycType } = {}) =>
  yup.object().shape({
    // Basic Info
    category_id: yup.string().required("Category is required"),
    subcategory_id: yup.string().required("Subcategory is required"),
    name: yup.string().required("Product name is required"),
    description: yup.string().nullable(),
    location: yup.string().required("Location is required"),

    // Pricing & Quantity
    price: asNullableNumber(),
    quantity: yup
      .string()
      .matches(/^\d+(\.\d{1,3})?$/, "Up to 3 decimal places allowed")
      .nullable(),

    unit_of_measurement: yup.string().nullable(),
    calc_unit: yup.string().nullable(),
    currency: yup.string().nullable(),

    // Shipping / Trade Terms
    incoterm: yup.string().nullable(),
    payment_term: yup.string().nullable(),
    lead_time: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? null : value
      )
      .min(1, "Lead time must be at least 1 day")
      .nullable(),
    delivery_port: yup.string().nullable(),
    delivery_radius: yup.number().nullable(),

    // Technical & Metal Fields
    metal_code: yup.string().nullable(),
    sorting_type: yup.string().nullable(),
    attachment: yup.string().nullable(),
    waste_percentage: yup.string().nullable(),
    waste_range: yup.string().nullable(),
    toxic_level: yup.string().nullable(),
    hazard_types: yup.array().of(yup.string()).nullable(),

    // Pricing & Weight
    price_per_unit: yup.string().nullable(),
    weight: yup.string().nullable(),
    calc_weight: yup.string().nullable(),
    estimated_price: yup.string().nullable(),

    // Specifications
    specifications: yup
      .array()
      .of(
        yup.object().shape({
          value: yup.string().nullable(),
          label: yup.string().nullable(),
          input_type: yup.string().nullable(),
          field_id: asNullableNumber(),
        })
      )
      .nullable(),

    // FAQs
    faqs: yup
      .array()
      .of(
        yup.object().shape({
          question: yup.string().nullable(),
          answer: yup.string().nullable(),
        })
      )
      .nullable(),

    // Bidding Fields
    is_reverse_auction: yup.boolean().nullable(),
    minimum_asking_price: asNullableNumber(),
    bid_increment: asNullableNumber(),
    bid_closed_at: yup.string().nullable(),
    reverse_close_price: asNullableNumber(),
    auction_status: yup
      .string()
      .oneOf(["open", "closed", "pending", "cancelled"])
      .nullable(),

    // Media
    image: yup.mixed().nullable(),
    video: yup.mixed().nullable(),
  });

export default productDraftSchema;
